import BlockItem from '@/components/BlockItem';
import { ActionList, DroppableContainer, SortableItemContainer } from '@/components/DragAndDrop';
import ActionListItem from '@/components/DragAndDrop/ActionListItem';
import EmptyActionList from '@/components/EmptyActionList';
import { keys } from '@/gql/global/keys';
import { onMutateBlockStatus } from '@/services/myPlan/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { Action } from '@/types/actions';
import { DnDBlockItemProps } from '@/types/dragAndDrop';
import {
  calculateTotalDuration,
  calculateTotalDurationWithRecurringInRange,
  getCanceledActions,
  getCompletedActions,
  getDailyActions,
  getIncompleteActions,
  getTotalDurationOfStarredActionsInRange,
  getWeeklyActions,
} from '@/utils/action';
import {
  endOfTheWeek,
  getRecurringInstancesInRangeCompletionSummary,
  hasRecurringOccurrenceInRange,
  startOfTheWeek,
} from '@/utils/calendar';
import { getTotalDurationOfStarredActions, validateRecurrence } from '@/utils/events';
import {
  appendRecurringRangeCompleteShadows,
  filterIncompleteActionsForRecurringWeekRange,
  mergeWeeklyRecurringCompletedShadows,
} from '@/utils/recurringCompletedShadow';
import { humanDuration, isOnProjectDetailPage } from '@/utils/index';
import { makeBlockItemKey } from '@/utils/myplan';
import rem from '@/utils/rem';
import { invalidateQueries } from '@/utils/tanStackQuery';
import { Text } from '@chakra-ui/react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { endOfDay, startOfDay } from 'date-fns';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const DnDBlockItem = ({
  isArchived = false,
  actions,
  block,
  sortable = false,
  index,
  weeklyPlanId,
  onCompleteBlock,
  onUncompleteBlock,
  openSetUncompletedActionsModal,
  onSelectBlock,
  actionModalProps,
  onClickAddAction,
  onClickRemoveFromBlock,
  showCompletedActions = true,
  withScheduleButton = false,
  openEditBlockModal,
  openDuplicateBlockModal,
  onDeleteBlock,
  draggable,
  collapsedByDefault = false,
  showCanceledActions = false,
  disableRecurringStatusChanges = false,
  recurringWeekContext = null,
}: DnDBlockItemProps) => {
  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);
  const isDailyPlanPage = useIsDailyPlanPage();
  const { t } = useTranslation('DnDBlockItem');

  const key = makeBlockItemKey(block.id);

  const onUpdateBlock = useCallback(() => {
    if (isOnProjectDetailPage()) {
      if (!block.projectId) return;
      invalidateQueries([keys.project.detail(block.projectId).queryKey]);
    }
    onMutateBlockStatus(block.id, false);
  }, [block.id, block.projectId]);

  const blockActions = useMemo(() => {
    if (isDailyPlanPage) {
      return getDailyActions(actions, selectedDate);
    }

    if (weeklyPlanId) {
      return getWeeklyActions(actions, weeklyPlanId);
    }

    return actions;
  }, [actions, selectedDate, weeklyPlanId, isDailyPlanPage]);

  const { incompleteActions, completedActions } = useMemo(() => {
    if (!blockActions?.length) {
      return { incompleteActions: [] as Action[], completedActions: [] as Action[] };
    }

    let incomplete = getIncompleteActions(blockActions, 'blockOrder');
    const completedBase = getCompletedActions(blockActions, 'modifiedAt');

    if (isDailyPlanPage) {
      const rangeStart = startOfDay(selectedDate);
      const rangeEnd = endOfDay(selectedDate);
      incomplete = incomplete.filter((a) => {
        if (!a?.event?.recurrence?.rules?.length) return true;
        if (!validateRecurrence(a.event?.recurrence)) return true;
        if (!hasRecurringOccurrenceInRange(a, rangeStart, rangeEnd)) return false;
        const summary = getRecurringInstancesInRangeCompletionSummary(a, rangeStart, rangeEnd);
        if (summary.inRangeCount > 0 && summary.allCompleteInRange) return false;
        return true;
      });
      return {
        incompleteActions: incomplete,
        completedActions: appendRecurringRangeCompleteShadows(
          completedBase,
          blockActions,
          rangeStart,
          rangeEnd,
          { requireAllCompleteInRange: true },
        ),
      };
    }

    const rangeStart = startOfTheWeek(selectedDate);
    const rangeEnd = endOfTheWeek(selectedDate);
    const incompleteFiltered = filterIncompleteActionsForRecurringWeekRange(incomplete, rangeStart, rangeEnd);

    return {
      incompleteActions: incompleteFiltered,
      completedActions: mergeWeeklyRecurringCompletedShadows(
        incompleteFiltered,
        completedBase,
        blockActions,
        rangeStart,
        rangeEnd,
      ),
    };
  }, [blockActions, isDailyPlanPage, selectedDate]);

  const canceledActions = useMemo(
    () => (blockActions?.length ? getCanceledActions(blockActions, 'modifiedAt') : []),
    [blockActions],
  );

  if (!blockActions?.length) {
    return <></>;
  }

  const incompleteForDuration = incompleteActions.filter((action) => !action.isCanceled);
  const blockTotalMinutes = isDailyPlanPage
    ? calculateTotalDuration(incompleteForDuration)
    : calculateTotalDurationWithRecurringInRange(
        incompleteForDuration,
        startOfTheWeek(selectedDate),
        endOfTheWeek(selectedDate),
      );
  const blockStarredTotal = isDailyPlanPage
    ? getTotalDurationOfStarredActions(incompleteForDuration)
    : getTotalDurationOfStarredActionsInRange(
        incompleteForDuration,
        startOfTheWeek(selectedDate),
        endOfTheWeek(selectedDate),
      );

  const blockItem = (
    <BlockItem
      item={block}
      totalDuration={humanDuration(blockTotalMinutes * 60)}
      starredActionsTotalDuration={blockStarredTotal}
      onDeleteBlock={onDeleteBlock}
      collapsedByDefault={collapsedByDefault}
      incompleteActions={
        <SortableContext items={incompleteActions} strategy={verticalListSortingStrategy}>
          <Text textStyle="xsmall" marginBottom={rem(16)} color="text-secondary" textTransform="uppercase">
            {t('titles.massiveActionPlan')}
          </Text>
          <ActionList
            key={key}
            emptyListComponent={
              <DroppableContainer
                id={key}
                payload={{
                  containerId: 'block-empty',
                  blockId: block.id,
                  category: block.category,
                  projectId: block.projectId,
                }}
              >
                <EmptyActionList
                  title={isEmpty(completedActions) ? t('empty.noCurrentActions') : t('empty.allActionsCompleted')}
                  message={isEmpty(completedActions) ? t('empty.clickToAddAction') : undefined}
                  motivational="small"
                />
              </DroppableContainer>
            }
            isEmpty={isEmpty(incompleteActions)}
          >
            {incompleteActions
              .filter((action) => !action.isCanceled)
              .map((action, index) => (
                <ActionListItem
                  disabled={isArchived}
                  key={action.id}
                  item={{ ...action, block }}
                  index={index}
                  dndPayload={{
                    blockId: block.id,
                    containerId: `block-${block.id}`,
                    index,
                    projectId: action?.projectId,
                  }}
                  onUpdateBlock={onUpdateBlock}
                  actionModalProps={actionModalProps}
                  onClickRemoveFromBlock={(actionId) => onClickRemoveFromBlock(actionId, block.id)}
                  withScheduleButton={withScheduleButton}
                  disableRecurringStatusChanges={
                    disableRecurringStatusChanges || validateRecurrence(action.event?.recurrence)
                  }
                  recurringWeekContext={recurringWeekContext}
                />
              ))}
          </ActionList>
        </SortableContext>
      }
      completedActions={
        <SortableContext items={completedActions}>
          <ActionList
            key={key}
            emptyListComponent={<EmptyActionList message={t('empty.noCompleteActions')} />}
            isEmpty={isEmpty(completedActions)}
          >
            {completedActions.map((action, index) => (
              <ActionListItem
                key={action.id}
                item={{ ...action, block }}
                index={index}
                dndPayload={{
                  blockId: block.id,
                  containerId: `block-${block.id}`,
                  index,
                  projectId: action?.projectId,
                }}
                onUpdateBlock={onUpdateBlock}
                onClickRemoveFromBlock={(actionId) => onClickRemoveFromBlock(actionId, block.id)}
                readOnly
                withScheduleButton={withScheduleButton}
                disableRecurringStatusChanges={
                  disableRecurringStatusChanges || validateRecurrence(action.event?.recurrence)
                }
                recurringWeekContext={recurringWeekContext}
              />
            ))}
          </ActionList>
        </SortableContext>
      }
      canceledActions={
        showCanceledActions && (
          <SortableContext items={canceledActions}>
            <ActionList
              key={key}
              emptyListComponent={<EmptyActionList message={t('empty.noCanceledActions')} />}
              isEmpty={isEmpty(canceledActions)}
            >
              {canceledActions.map((action, index) => (
                <ActionListItem key={action.id} item={{ ...action, block }} index={index} />
              ))}
            </ActionList>
          </SortableContext>
        )
      }
      numberOfCompletedActions={completedActions?.length}
      numberOfCanceledActions={showCanceledActions ? canceledActions?.length : 0}
      showCompletedActions={showCompletedActions}
      onUncompleteBlock={onUncompleteBlock}
      onCompleteBlock={onCompleteBlock}
      openSetUncompletedActionsModal={openSetUncompletedActionsModal}
      onSelectBlock={onSelectBlock}
      actions={actions}
      onClickAddAction={onClickAddAction}
      openEditBlockModal={openEditBlockModal}
      openDuplicateBlockModal={openDuplicateBlockModal}
      draggable={draggable}
    />
  );

  const itemProps = {
    id: block.id,
    payload: {
      blockId: block.id,
      category: block.category,
      containerId: 'blocks',
      index,
      projectId: block.projectId,
    },
  };

  return sortable ? (
    <SortableItemContainer {...itemProps}>{blockItem}</SortableItemContainer>
  ) : (
    <DroppableContainer {...itemProps}>{blockItem}</DroppableContainer>
  );
};

export default DnDBlockItem;
